import { VERIFICATION_ACCOUNT_TYPE } from '@app/domain/accountVerification';
import { SergeeSearchResult } from '@mortee/domain/morteeAccountVerification';
import { UnifiedPayee } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import AccountVerificationInitialMissingFieldsPageV2 from './AccountVerificationInitialMissingFieldsPageV2';
import AccountVerificationInitialPageV2 from './AccountVerificationInitialPageV2';
import AccountVerificationQuickCheckResultPage from './AccountVerificationQuickCheckResultPage';
import { AccountVerificationForm, handleAccountVerificationSubmit } from './searchPayeeUtils';
import { handleApprove, handleCancelKnoxValidation, ResultPageActions, ResultPageState } from './resultPageUtils';
import { downloadResourceByFileContent } from '@app/utils/fileUtils';
import { FileContentTypes, translateContentTypeToFileExtension } from '@app/domain/files';
import { downloadAccountVerificationByUniformId } from '@mortee/services/accountVerificationService';
import { removeIrrelevantAccountDetailsFields } from '@app/domain/accountsDetailsHelpers';

export default function AccountVerificationInitialPageV2Container(): JSX.Element {
  const initialFormData: AccountVerificationForm & { countryCode: string } = {
    accountNumber: '',
    accountType: VERIFICATION_ACCOUNT_TYPE.domestic,
    country: '',
    iban: '',
    knoxOrTaxId: '',
    payeeName: '',
    bankCode: '',
    branchCode: '',
    swiftBic: '',
    taxId: '',
    countryCode: '',
  };

  const [showResult, setShowResult] = useState<boolean>(false);
  const [showMissingFields, setShowMissingFields] = useState<boolean>(false);
  const [missingFields, setMissingFields] = useState<string[]>(['bankCode', 'branchCode']);
  const [formDataPersistent, setFormDataPersistent] = useState<AccountVerificationForm>(initialFormData);
  const [selectedPayee, setSelectedPayee] = useState<UnifiedPayee | null>(null);
  const [verificationResult, setVerificationResult] = useState<SergeeSearchResult | null>(null);
  const [, setVerificationError] = useState<Error | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const isInitialSelection = useRef(true);
  const previousSelectedPayee = useRef<UnifiedPayee | null>(null);
  const isUpdatingFromSelection = useRef(false);
  const [countryCode, setCountryCode] = useState<string>('');
  const [preparingReportDownload, setPreparingReportDownload] = useState<boolean>(false);

  // Result page state
  const [resultPageState, setResultPageState] = useState<ResultPageState>({
    isApproving: false,
    isApproved: false,
    showMatchingDialog: false,
    matchingResults: null,
  });

  const resultPageActions: ResultPageActions = {
    setIsApproving: (isApproving: boolean) => setResultPageState((prev) => ({ ...prev, isApproving })),
    setIsApproved: (isApproved: boolean) => setResultPageState((prev) => ({ ...prev, isApproved })),
    setShowMatchingDialog: (showMatchingDialog: boolean) => setResultPageState((prev) => ({ ...prev, showMatchingDialog })),
    setMatchingResults: (matchingResults) => setResultPageState((prev) => ({ ...prev, matchingResults })),
  };

  // Sync formDataPersistent when a new payee is selected
  useEffect(() => {
    if (selectedPayee && selectedPayee !== previousSelectedPayee.current) {
      // A new payee was selected - update formDataPersistent to match
      const payeeName = selectedPayee.names?.[0]?.name || '';
      const knoxId = selectedPayee.strongLegalIdentifies?.KnoxId?.[0]?.value || '';
      const taxId = selectedPayee.strongLegalIdentifies?.TaxId?.[0]?.value || '';
      const knoxOrTaxId = knoxId || taxId;

      // Set flag to prevent validation from clearing the selection
      isUpdatingFromSelection.current = true;

      setFormDataPersistent((prev) => ({
        ...prev,
        payeeName,
        knoxOrTaxId,
      }));

      previousSelectedPayee.current = selectedPayee;
      isInitialSelection.current = false;
    } else if (!selectedPayee) {
      previousSelectedPayee.current = null;
      isInitialSelection.current = true;
      isUpdatingFromSelection.current = false;
    }
  }, [selectedPayee]);

  // Clear selectedPayee if form data is manually changed and doesn't match the selected payee
  useEffect(() => {
    // Skip validation if we're updating formDataPersistent due to a payee selection
    if (isUpdatingFromSelection.current) {
      isUpdatingFromSelection.current = false;
      return;
    }

    if (selectedPayee && !isInitialSelection.current) {
      const payeeName = selectedPayee.names?.[0]?.name || '';
      const knoxId = selectedPayee.strongLegalIdentifies?.KnoxId?.[0]?.value || '';
      const taxId = selectedPayee.strongLegalIdentifies?.TaxId?.[0]?.value || '';
      const knoxOrTaxId = knoxId || taxId;

      // Only clear if form data was manually changed (not matching the selected payee)
      if (formDataPersistent.payeeName !== payeeName || formDataPersistent.knoxOrTaxId !== knoxOrTaxId) {
        setSelectedPayee(null);
        previousSelectedPayee.current = null;
      }
    }
  }, [formDataPersistent.payeeName, formDataPersistent.knoxOrTaxId, selectedPayee]);

  const handleResultPageBack = (): void => {
    setShowResult(false);
    // Always clear form values when going back
    setFormDataPersistent(initialFormData);
    setSelectedPayee(null);
    setCountryCode('');
    setVerificationResult(null);
    setVerificationError(null);

    // Reset result page state
    setResultPageState({
      isApproving: false,
      isApproved: false,
      showMatchingDialog: false,
      matchingResults: null,
    });
  };

  const showToast = (toastmessage: string): void => {
    toast.success(toastmessage, {
      duration: 5000,
      position: 'top-right',
      style: { whiteSpace: 'pre-wrap' },
    });
  };

  const handleResultPageApprove = async (): Promise<void> => {
    await handleApprove(verificationResult, resultPageActions, showToast, setVerificationResult);
  };

  const handleResultPageCancelKnoxValidation = async (): Promise<void> => {
    await handleCancelKnoxValidation(
      verificationResult,
      resultPageState.matchingResults,
      resultPageActions.setShowMatchingDialog,
      showToast,
    );
  };

  const handleResultPageContinueKnoxValidation = (): void => {
    resultPageActions.setShowMatchingDialog(false);
    showToast('Knox verify will continue.');
  };

  const handleResultPageDownloadBankAccountCertificate = async (): Promise<void> => {
    setPreparingReportDownload(true);

    try {
      if (!selectedPayee?.ids && !verificationResult?.validatedPayeeUniformId) {
        console.error('No payee IDs available for download');
        return;
      }

      const payeeIds = {
        validatedId: verificationResult?.validatedPayeeUniformId || selectedPayee?.ids.validatedId,
        privateIds: selectedPayee?.ids.privateIds || [],
      };

      let accountDetails: Partial<MorteeAccountDetailsExtended> = {
        accountNumber: formDataPersistent.accountNumber || undefined,
        bankCode: formDataPersistent.bankCode || undefined,
        branchCode: formDataPersistent.branchCode || undefined,
        iban: formDataPersistent.iban || undefined,
        swiftCode: formDataPersistent.swiftBic || undefined,
        countryCode: countryCode,
        localFormat: formDataPersistent.localFormat || undefined,
      };

      accountDetails = removeIrrelevantAccountDetailsFields(accountDetails);
      const resource: NamedResource = await downloadAccountVerificationByUniformId(payeeIds, accountDetails);

      if (resource) {
        const { name, resourceData } = resource;

        downloadResourceByFileContent(
          resourceData.content,
          resourceData.mediaType ?? FileContentTypes.APPLICATION_PDF,
          `${name}${translateContentTypeToFileExtension(resourceData.mediaType)}`,
        );
      }
    } finally {
      setPreparingReportDownload(false);
    }
  };

  if (showResult && formDataPersistent) {
    return (
      <AccountVerificationQuickCheckResultPage
        formData={formDataPersistent}
        verificationResult={verificationResult}
        isVerifying={isVerifying}
        resultPageState={resultPageState}
        onBack={handleResultPageBack}
        onApprove={handleResultPageApprove}
        onCancelKnoxValidation={handleResultPageCancelKnoxValidation}
        onContinueKnoxValidation={handleResultPageContinueKnoxValidation}
        downloadBankAccountCertificate={handleResultPageDownloadBankAccountCertificate}
        preparingReportDownload={preparingReportDownload}
      />
    );
  }

  if (showMissingFields && formDataPersistent) {
    return (
      <AccountVerificationInitialMissingFieldsPageV2
        formData={formDataPersistent}
        missingFields={missingFields}
        setCountryCode={setCountryCode}
        verificationResult={verificationResult}
        onSubmit={async (currentFormData: AccountVerificationForm): Promise<void> => {
          await handleAccountVerificationSubmit({
            currentFormData,
            selectedPayee,
            countryCode,
            setVerificationError,
            setShowResult,
            setIsVerifying,
            setVerificationResult,
            setFormDataPersistent,
            setMissingFields,
            setShowMissingFields,
          });
        }}
        onBack={(): void => setShowMissingFields(false)}
        onSkip={(): void => {
          setShowResult(true);
          setShowMissingFields(false);
        }}
      />
    );
  }

  return (
    <AccountVerificationInitialPageV2
      showResult={setShowResult}
      persistedData={formDataPersistent}
      setSelectedPayee={setSelectedPayee}
      setCountryCode={setCountryCode}
      onSubmitForm={async (currentFormData: AccountVerificationForm): Promise<void> => {
        await handleAccountVerificationSubmit({
          currentFormData,
          selectedPayee,
          countryCode,
          setVerificationError,
          setShowResult,
          setIsVerifying,
          setVerificationResult,
          setFormDataPersistent,
          setMissingFields,
          setShowMissingFields,
        });
      }}
    />
  );
}
